import registryJson from 'command-palette-registry';

import type {ActionItem, RegistryActionItem} from './types';

// For actions that need complex logic (API calls, stateful modals, etc.),
// add an entry here keyed by the action id from registry.json.
// These take priority over the auto-generated handlers below.
const ACTION_HANDLERS: Record<string, (searchQuery?: string) => string | void> = {
    'action-ai-research-lead': () => {
        window.dispatchEvent(
            new CustomEvent('teamAiOpenAgent', {detail: {agentKey: 'LEAD_RESEARCH'}})
        );
    },
    'action-ai-email-sequence': () => {
        window.dispatchEvent(
            new CustomEvent('teamAiOpenAgent', {detail: {agentKey: 'EMAIL_SEQUENCE'}})
        );
    },
};

/**
 * Strip known action keyword prefixes from a search query to extract
 * the actual user prompt. For example, given the query
 * "generate report for Where are my top leads coming from?" and
 * keywords ["generate report", "report for", ...], this returns
 * "Where are my top leads coming from?".
 *
 * Common filler words ("for", "about", "on") between the keyword and
 * the prompt are also stripped.
 */
function extractPromptFromQuery(
    searchQuery: string | undefined,
    keywords: string[]
): string | undefined {
    if (!searchQuery) return undefined;

    const queryLower = searchQuery.toLowerCase().trim();

    // Sort keywords longest-first so we strip the most specific prefix
    const sorted = [...keywords].sort((a, b) => b.length - a.length);

    for (const kw of sorted) {
        if (queryLower.startsWith(kw.toLowerCase())) {
            let remainder = searchQuery.slice(kw.length).trim();

            // Strip optional filler words that connect keyword to prompt
            remainder = remainder.replace(/^(for|about|on|with|of)\b\s*/i, '').trim();

            return remainder || undefined;
        }
    }

    // No keyword prefix found — return the whole query as a prompt
    return searchQuery.trim() || undefined;
}

function buildHandler(item: RegistryActionItem): (searchQuery?: string) => string | void {
    const override = ACTION_HANDLERS[item.id];
    if (override) {
        return override;
    }

    if (item.actionType === 'hash') {
        return () => {
            window.location.hash = item.actionValue;
        };
    }

    if (item.actionType === 'navigate') {
        return () => item.actionValue;
    }

    // AI-superpowered actions: navigate with query param context
    if (item.actionType === 'ai') {
        return (searchQuery?: string) => {
            if (item.aiParam === 'prompt') {
                const prompt = extractPromptFromQuery(searchQuery, item.keywords);
                if (prompt) {
                    return `${item.actionValue}?prompt=${encodeURIComponent(prompt)}`;
                }
            }

            // Navigate to the AI feature page (with or without params)
            return item.actionValue;
        };
    }

    // "custom" actionType requires a matching entry in ACTION_HANDLERS above.
    // If we reach here, it means a custom action was defined in registry.json
    // without a corresponding handler — it will be a no-op.
    return () => undefined;
}

/**
 * Action items for the command palette, sourced from the shared
 * `registry.json` file with handler logic defined in TypeScript.
 */
export const ACTIONS_REGISTRY: ActionItem[] = (registryJson.actions as RegistryActionItem[]).map(
    (item) => ({
        id: item.id,
        label: item.label,
        keywords: item.keywords,
        section: item.section,
        isAiAction: item.actionType === 'ai' || item.section === 'AI',
        getAction: buildHandler(item),
    })
);
